/**
 * Widget Configuration Types
 */

export type WidgetMode = 'page' | 'block' | 'highlight';
export type VoiceId = 'heart' | 'michael' | 'lily' | 'leo' | 'mia' | 'theo'
  // Standard voices (Free and Starter plans)
  | 'ava' | 'sam'
  // Legacy ids, still accepted (normalized by normalizeVoice)
  | 'alloy' | 'echo' | 'onyx' | 'fable' | 'nova' | 'shimmer';

/** Old voice ids → current ones (older embeds, WordPress settings, saved visitor settings) */
const LEGACY_VOICES: Record<string, VoiceId> = { alloy: 'lily', echo: 'leo', nova: 'mia', fable: 'theo', onyx: 'leo', shimmer: 'lily' };

export function normalizeVoice<T extends string | undefined>(voice: T): T {
  if (!voice) return voice;
  return ((LEGACY_VOICES[voice.toLowerCase()] as string | undefined) ?? voice) as T;
}
export type ThemeMode = 'light' | 'dark' | 'auto';
export type ControlsPosition = 'floating' | 'inline' | 'hidden';
export type PlayerPosition = 'top' | 'bottom';
export type TogglePosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

// Toolbar tool identifiers
/** Every toolbar tool, in toolbar order — the default for data-tools */
export const DEFAULT_TOOLS: ToolId[] = ['play', 'simplify', 'vocabSimplify', 'pageTheme', 'fontSize', 'fontSelector', 'screenMask', 'magnifier', 'screenshotReader', 'dictionary', 'translate', 'summary', 'download', 'clickListen', 'formReader', 'settings'];

export type ToolId = 'play' | 'simplify' | 'vocabSimplify' | 'pageTheme' | 'fontSize' |
  'fontSelector' | 'screenMask' | 'magnifier' | 'screenshotReader' | 'dictionary' | 'translate' | 'summary' | 'download' | 'clickListen' | 'formReader' | 'settings';

// Page theme presets (applied to host page, not widget UI)
export type PageThemeId = 'default' | 'black-on-white' | 'white-on-black' |
  'black-on-yellow' | 'black-on-light-blue' | 'black-on-pink' | 'black-on-cream' |
  'blue-on-yellow' | 'blue-on-white' | 'blue-on-pink' | 'red-on-white' |
  'yellow-on-blue' | 'yellow-on-black';

// Font family options
export type FontFamilyId = 'default' | 'sans-serif' | 'serif' | 'monospace' | 'comic-sans' | 'opendyslexic';

// Toolbar theme preference
export type ToolbarTheme = 'auto' | 'light' | 'dark';

// Toolbar settings persisted to localStorage
export interface ToolbarSettings {
  pageTheme: PageThemeId;
  fontSize: number; // percentage 80-200
  fontFamily: FontFamilyId;
  maskHeight: number; // lines 3-8
  /** Playback volume 0–100 (default 100) */
  volume?: number;
  /** Extra letter/word/line spacing, 0 (page default) to 4 (WCAG 1.4.12 text-spacing values) */
  textSpacing?: number;
  autoScroll: boolean;
  toolbarTheme: ToolbarTheme;
  /** Only set when the visitor explicitly picked a voice (otherwise config.voice applies) */
  voice?: VoiceId;
  /** True once the visitor picked a voice in Settings */
  voiceChosen?: boolean;
  /** Which highlights to show while reading */
  highlightMode?: 'wordsent' | 'word' | 'sent' | 'none';
  /** Swatch ids from ui/highlightStyles.ts */
  highlightWordColor?: string;
  highlightSentenceColor?: string;
  /** Keyboard shortcuts on/off (website widget; default on) */
  shortcutsEnabled?: boolean;
  /** Visitor's custom bindings (action → "alt+shift+KeyR"); defaults in ui/shortcuts.ts */
  shortcuts?: Record<string, string>;
}

// Tool interface implemented by all toolbar tools
export interface Tool {
  id: ToolId;
  label: string;
  icon: string;
  isToggleable: boolean;
  isOverlay: boolean; // Overlay tools (simplify, mask, magnifier) can coexist; dropdown tools are mutually exclusive
  isActive: boolean;
  activate(): void;
  deactivate(): void;
  destroy(): void;
}

// Word highlight event data (for magnifier/mask sync)
export interface WordHighlightEvent {
  word: string;
  sentence: string;
  wordIndex: number;
  timestamp: number;
}

export interface ThemeConfig {
  mode?: ThemeMode;
  primaryColor?: string;
  secondaryColor?: string;
  textColor?: string;
  backgroundColor?: string;
  borderRadius?: number;
}

export interface ReadingVoxConfig {
  // Authentication (at least one required)
  apiKey?: string;
  licenseKey?: string;
  studentEmail?: string;

  // Operating mode
  mode?: WidgetMode;

  // TTS (browser fallback is automatic)
  fallbackToBrowser?: boolean;

  // Text extraction
  selectors?: string[];
  ignoreSelectors?: string[];

  /**
   * Platform preset. 'canvas' / 'moodle' = LMS content areas only, sensitive
   * pages excluded, dynamic content observed, URLs sent without query/hash.
   * Website builds auto-apply the matching preset on Canvas / Moodle pages when
   * neither preset nor selectors are given; 'none' disables that.
   */
  preset?: 'canvas' | 'moodle' | 'none';

  /**
   * Language of the content: 'auto' (default) reads the page/element `lang`
   * attribute; or a code like 'es'. Spanish, French, Italian, Portuguese, Hindi,
   * Japanese and Chinese use natural voices; other languages the browser voice.
   */
  contentLanguage?: string;

  /**
   * Interface language for the toolbar and panels (data-ui-lang), e.g. 'es'.
   * Default: the visitor's browser language if supported, else the page's, else English.
   * A visitor's choice in Settings always wins.
   */
  uiLanguage?: string;

  /**
   * Add a Listen button after links to PDFs; it opens the document in a reader
   * view and reads it aloud (data-documents). Default false (opt-in).
   */
  documentLinks?: boolean;

  /** Keyboard shortcuts (website default true; the Chrome extension uses Chrome commands) */
  keyboardShortcuts?: boolean;
  /** Don't activate at all when location.pathname matches any of these */
  excludePaths?: (RegExp | string)[];
  /** Content blocks inside (or containing) elements matching this selector are skipped */
  blockExclude?: string;
  /** Only read matched content (no <body> fallback; selection menu only inside content) */
  contentOnly?: boolean;
  /** Watch for content blocks added after load (debounced MutationObserver) */
  observeContent?: boolean;
  /**
   * Read iframes as part of the page (default true): same-origin frames
   * directly, frames on the same root domain (or in frameOrigins) that load
   * ReadingVox themselves through frame mode (data-frames="false" turns it off).
   */
  readFrames?: boolean;
  /** Extra origins allowed for frame mode, e.g. ["https://partner.example.org"] (data-frame-origins) */
  frameOrigins?: string[];
  /** Send page URLs to the API without ?query and #hash */
  stripUrlParams?: boolean;

  // Audio settings
  voice?: VoiceId;
  speed?: number; // 0.5 - 2.0

  // UI settings
  showControls?: ControlsPosition;
  position?: PlayerPosition;
  autoScroll?: boolean;
  highlightWords?: boolean;
  highlightSentences?: boolean;
  theme?: ThemeMode | ThemeConfig;
  showToggle?: boolean;
  togglePosition?: TogglePosition;
  /** Distance (px) of a bottom-* toggle button from the viewport bottom (default 72) */
  toggleBottomOffset?: number;
  /** Pulse the floating button for first-time visitors until they click it (default true) */
  pulse?: boolean;
  alwaysShowPlayer?: boolean;

  // Caching
  cacheAudio?: boolean;
  cacheSize?: number; // MB

  // Events
  onReady?: () => void;
  onPlay?: (timestamp: number) => void;
  onPause?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
  onProgress?: (percent: number) => void;
  onWordHighlight?: (word: string, timestamp: number) => void;

  // Toolbar
  toolbar?: boolean;
  toolbarPosition?: 'top' | 'bottom';
  /** Keep the toolbar clear of a fixed left nav this many px wide (viewports ≥768px) */
  toolbarInsetLeft?: number;
  /** Keep the toolbar clear of a fixed bottom-right control this many px wide (viewports ≥768px) */
  toolbarInsetRight?: number;
  enabledTools?: ToolId[];

  // Advanced
  apiEndpoint?: string;
  debug?: boolean;
}

export interface ReadingVoxDefaults {
  mode: WidgetMode;
  fallbackToBrowser: boolean;
  selectors: string[];
  ignoreSelectors: string[];
  voice: VoiceId;
  speed: number;
  showControls: ControlsPosition;
  position: PlayerPosition;
  autoScroll: boolean;
  highlightWords: boolean;
  highlightSentences: boolean;
  theme: ThemeMode;
  showToggle: boolean;
  togglePosition: TogglePosition;
  pulse: boolean;
  alwaysShowPlayer: boolean;
  toolbar: boolean;
  toolbarPosition: 'top' | 'bottom';
  enabledTools: ToolId[];
  cacheAudio: boolean;
  cacheSize: number;
  apiEndpoint: string;
  debug: boolean;
}

export const DEFAULT_CONFIG: ReadingVoxDefaults = {
  mode: 'highlight',
  fallbackToBrowser: true,
  selectors: ['article', 'main', '[role="main"]', '.content'],
  ignoreSelectors: [
    'nav', 'footer', '.ads', '.no-read', 'script', 'style',
    '[role="alert"]', '[role="complementary"]', '[role="note"]',
    '.alert', '.callout', '.notice', '.warning', '.info-box',
    'aside', '.sidebar'
  ],
  voice: 'heart',
  speed: 1.0,
  showControls: 'floating',
  position: 'bottom',
  autoScroll: true,
  highlightWords: true,
  highlightSentences: false,
  theme: 'auto',
  showToggle: true,
  togglePosition: 'bottom-right',
  pulse: true,
  alwaysShowPlayer: false,
  toolbar: true,
  toolbarPosition: 'top',
  enabledTools: [...DEFAULT_TOOLS],
  cacheAudio: true,
  cacheSize: 50, // MB
  apiEndpoint: '/api/v1',
  debug: false
};
